'use client';

import { SkipRenderOnServer } from '@/react/components/utils/SkipRenderOnServer';
import { Icon, IconTypes } from '@/react/components/core-ui/Icon';
import { useMediaPlayerActions } from '@/react/components/core-ui/Video/MediaPlayer';
import { useBrandLocaleValue } from '@/react/hooks/useBrandLocaleValue';
import ConfigurationService from '@/services/isomorphic/ConfigurationService';
import { msg } from '@/services/isomorphic/I18NService';
import { memo, type FC } from 'react';

import S from './styles.module.scss';
import { content_video_fullscreen } from "@/lang/__generated__/ahnu/content_video_fullscreen";

/**
 * A client-only button for viewing media in fullscreen.
 *
 * **Note:** If the client does not support fullscreen, this button
 * will not be rendered.
 */
const ClientOnlyFullScreenButton: FC = () => {
  const { enterFullscreen } = useMediaPlayerActions();

  /**
   * Icon to use, if we we're showing the fullscreen icon.
   */
  const FullscreenIconType = useBrandLocaleValue<IconTypes>(
    () => ({
      default: IconTypes.FullScreen,
      AHNU: IconTypes.FullScreenLight
    }),
    []
  );

  const uiConfig = ConfigurationService.getConfig('ui');
  const showFullscreenIcon = uiConfig.getSetting(
    'video.showFullscreenIcon'
  ).value;

  if (!document.fullscreenEnabled || !showFullscreenIcon) return null;

  return (
    <Icon
      onClick={enterFullscreen}
      className={S.icon}
      icon={FullscreenIconType}
      ariaLabel={msg(content_video_fullscreen)}
    />
  );
};

/** A button for entering fullscreen mode for some media. */
export const FullScreenButton: FC = memo(() => {
  return (
    <SkipRenderOnServer>
      <ClientOnlyFullScreenButton />
    </SkipRenderOnServer>
  );
});
